import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";

const DEMO_LINK_BASE_URL = "https://demo.jobinfonetwork.com/#/demo/";

// Links meant for the prospect. They must never carry ?source=admin, or the
// prospect's visits would count as admin views and send no alert.
// ?skipIntro=1 opens the player straight on the Member to Member Connection
// demo, past the ministry introduction.
const prospectDemoLinks = (demoId) => [
  {
    key: "demo",
    label: "Demo",
    hint: "Starts with the ministry introduction, like the marketing email.",
    url: `${DEMO_LINK_BASE_URL}${demoId}`,
  },
  {
    key: "member",
    label: "Member to Member Connection",
    hint: "Skips the introduction and opens on the connection demo.",
    url: `${DEMO_LINK_BASE_URL}${demoId}?skipIntro=1`,
  },
];

// navigator.clipboard needs a secure context; fall back to a hidden textarea
// and execCommand so copying still works on plain http (e.g. local dev).
const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    if (!document.execCommand("copy")) throw new Error("Copy failed");
  } finally {
    document.body.removeChild(textarea);
  }
};

export default function CopyDemoLinkModal({ show, contact, onClose }) {
  // Which link was copied last, so its button can say "Copied!".
  const [copiedKey, setCopiedKey] = useState(null);
  const [copyError, setCopyError] = useState(false);

  useEffect(() => {
    if (!show) {
      setCopiedKey(null);
      setCopyError(false);
    }
  }, [show]);

  if (!contact?.demo_id) return null;

  const handleCopy = async (link) => {
    try {
      await copyText(link.url);
      setCopiedKey(link.key);
      setCopyError(false);
    } catch (error) {
      console.error("Error copying demo link:", error);
      setCopiedKey(null);
      setCopyError(true);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered className="custome-modal">
      <div className="message-container custome-msg-container">
        <div className="message-header-box">
          <h4 className="text-truncate">
            Demo Links{contact.group_name ? ` — ${contact.group_name}` : ""}
          </h4>
          <div className="messages-modal-actions">
            <Button className="btn" onClick={onClose}>
              <FaTimes />
            </Button>
          </div>
        </div>
        <div className="message-body">
          {contact.unsubscribed && (
            <p className="text-danger">
              This contact has unsubscribed from marketing emails.
            </p>
          )}
          {prospectDemoLinks(contact.demo_id).map((link) => (
            <Form.Group key={link.key} controlId={`demoLink-${link.key}`}>
              <Form.Label className="mb-0">
                <strong>{link.label}</strong>
              </Form.Label>
              <small className="d-block text-muted mb-1">{link.hint}</small>
              <div className="d-flex">
                <Form.Control
                  type="text"
                  readOnly
                  value={link.url}
                  onFocus={(e) => e.target.select()}
                  className="inputclass form-control mr-2"
                />
                <Button
                  variant={copiedKey === link.key ? "success" : "primary"}
                  className="text-nowrap"
                  style={{ minWidth: "6.5rem" }}
                  onClick={() => handleCopy(link)}
                >
                  {copiedKey === link.key ? "Copied!" : "Copy"}
                </Button>
              </div>
            </Form.Group>
          ))}
          {copyError && (
            <p className="text-danger mb-2">
              Couldn't copy automatically. Click the link, then press Ctrl+C
              (⌘C on a Mac).
            </p>
          )}
          <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
            When the contact opens these links, the visits count as prospect
            views and send the usual alerts.
          </p>
        </div>
        <div className="message-footer text-right" style={{ display: "block" }}>
          <Button variant="secondary" type="button" size="xs" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
